import React, { useState, useEffect } from 'react';
import { adminSettingsAPI } from '../../../../services';
import './WebVersionManager.css';

const WebVersionManager = () => {
  const [webVersionSettings, setWebVersionSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    webVersion: 'web2',
    web2Config: {
      allowEmailSignup: true,
      allowGoogleAuth: true,
      requireEmailVerification: true
    },
    web3Config: {
      supportedChains: [],
      supportedTonNetworks: [],
      requiredChainId: null,
      requiredTonNetworkId: null,
      walletConnectProjectId: null
    },
    migrationSettings: {
      allowWeb2ToWeb3Migration: false,
      allowDualAuth: false
    }
  });

  // Preset EVM chain configurations
  const PRESET_EVM_CHAINS = {
    ethereum: {
      chainId: 1,
      name: 'Ethereum Mainnet',
      rpcUrl: 'https://eth.llamarpc.com',
      enabled: true,
      chainType: 'evm'
    },
    polygon: {
      chainId: 137,
      name: 'Polygon Mainnet',
      rpcUrl: 'https://polygon.llamarpc.com',
      enabled: true,
      chainType: 'evm'
    },
    bsc: {
      chainId: 56,
      name: 'BNB Smart Chain',
      rpcUrl: 'https://bsc.llamarpc.com',
      enabled: true,
      chainType: 'evm'
    },
    xaai: {
      chainId: 7777,
      name: 'XAAI',
      rpcUrl: 'https://xaai.wdtp.io',
      enabled: true,
      chainType: 'evm'
    }
  };

  // Preset TON network configurations
  const PRESET_TON_NETWORKS = {
    mainnet: {
      networkId: 'mainnet',
      name: 'TON Mainnet',
      workchain: 0,
      apiUrl: 'https://tonapi.io/v2',
      blockExplorerUrl: 'https://tonscan.org',
      nativeCurrency: { name: 'Toncoin', symbol: 'TON', decimals: 9 },
      enabled: true,
      isTestnet: false
    },
    testnet: {
      networkId: 'testnet',
      name: 'TON Testnet',
      workchain: 0,
      apiUrl: 'https://testnet.tonapi.io/v2',
      blockExplorerUrl: 'https://testnet.tonscan.org',
      nativeCurrency: { name: 'Toncoin', symbol: 'TON', decimals: 9 },
      enabled: true,
      isTestnet: true
    }
  };

  // Fetch web version settings
  const fetchWebVersionSettings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching web version settings...');
      const response = await adminSettingsAPI.getWebVersion();
      console.log('Web version settings response:', response.data);
      
      const settingsData = response.data.data || response.data;
      console.log('Extracted settings data:', settingsData);
      
      setWebVersionSettings(settingsData);
      setEditFormData(settingsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching web version settings:', err);
      
      const errorDetails = {
        status: err.response?.status,
        statusText: err.response?.statusText,
        message: err.response?.data?.message || err.message,
        url: err.config?.url,
        method: err.config?.method
      };
      console.error('Detailed error info:', errorDetails);
      
      let userMessage = 'Failed to fetch web version settings';
      if (err.response?.status === 404) {
        userMessage = 'Web version settings endpoint not implemented on server. Using demo mode with default settings.';
      } else if (err.response?.status === 401) {
        userMessage = 'Authentication failed. Please check your admin credentials.';
      } else if (err.response?.status === 500) {
        userMessage = 'Server error. Please contact your system administrator.';
      } else if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
        userMessage = 'Cannot connect to server. Please check if the server is running.';
      }
      
      // Only set error if it's not a 404 (404 is expected when endpoint not implemented)
      if (err.response?.status !== 404) {
        const errorMessage = `${userMessage} (${err.response?.status || err.code || 'Unknown error'})`;
        setError(errorMessage);
      } else {
        setError(null); // Clear error for 404 since we show info box instead
      }

      const defaultSettings = {
        webVersion: 'web2',
        web2Config: {
          allowEmailSignup: true,
          allowGoogleAuth: true,
          requireEmailVerification: true
        },
        web3Config: {
          supportedChains: [],
          supportedTonNetworks: [],
          requiredChainId: null,
          requiredTonNetworkId: null,
          walletConnectProjectId: null
        },
        migrationSettings: {
          allowWeb2ToWeb3Migration: false,
          allowDualAuth: false
        },
        lastModified: null
      };
      setWebVersionSettings(defaultSettings);
      setEditFormData(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebVersionSettings();
  }, []);

  // Handle form submission
  const handleUpdateSettings = async () => {
    setSaving(true);
    setError(null);

    try {
      console.log('Updating web version settings:', editFormData);
      const response = await adminSettingsAPI.updateWebVersion(editFormData);
      console.log('Update response:', response.data);
      
      let settingsData;
      
      if (response.data.settings) {
        settingsData = response.data.settings;
      } else if (response.data.data) {
        settingsData = response.data.data;
      } else {
        settingsData = response.data;
      }
      
      if (!settingsData.webVersion && response.data.currentVersion) {
        settingsData.webVersion = response.data.currentVersion;
      }
      
      console.log('Updated settings data:', settingsData);
      
      if (!settingsData.webVersion || !settingsData.web2Config) {
        console.log('⚠️ Incomplete settings data after update, refetching...');
        setShowEditDialog(false);
        setError(null);
        fetchWebVersionSettings();
      } else {
        setWebVersionSettings(settingsData);
        setShowEditDialog(false);
        setError(null);
      }
    } catch (err) {
      console.error('Error updating web version settings:', err);
      
      const errorDetails = {
        status: err.response?.status,
        statusText: err.response?.statusText,
        message: err.response?.data?.message || err.message,
        url: err.config?.url,
        method: err.config?.method
      };
      console.error('Detailed update error info:', errorDetails);
      
      let userMessage = 'Failed to update web version settings';
      if (err.response?.status === 404) {
        userMessage = 'Web version settings endpoint not implemented on server. Changes cannot be saved.';
      } else if (err.response?.status === 401) {
        userMessage = 'Authentication failed. Please check your admin credentials.';
      } else if (err.response?.status === 500) {
        userMessage = 'Server error. Please contact your system administrator.';
      } else if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
        userMessage = 'Cannot connect to server. Please check your connection.';
      }
      
      // Only set error if it's not a 404 (404 is expected when endpoint not implemented)
      if (err.response?.status !== 404) {
        const errorMessage = `${userMessage} (${err.response?.status || err.code || 'Unknown error'})`;
        setError(errorMessage);
      } else {
        setError(null); // Clear error for 404 since endpoint not implemented
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle input changes
  const handleInputChange = (path, value) => {
    setEditFormData(prevData => {
      const newData = { ...prevData };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Add new EVM supported chain with preset
  const addSupportedChain = (presetKey = 'custom') => {
    if (presetKey === 'custom') {
      const newChain = {
        chainId: 1,
        name: 'Custom Chain',
        rpcUrl: '',
        enabled: true,
        chainType: 'evm'
      };
      
      setEditFormData(prevData => ({
        ...prevData,
        web3Config: {
          ...prevData.web3Config,
          supportedChains: [...(prevData.web3Config.supportedChains || []), newChain]
        }
      }));
    } else {
      const presetChain = PRESET_EVM_CHAINS[presetKey];
      if (presetChain) {
        setEditFormData(prevData => ({
          ...prevData,
          web3Config: {
            ...prevData.web3Config,
            supportedChains: [...(prevData.web3Config.supportedChains || []), { ...presetChain }]
          }
        }));
      }
    }
  };

  // Add new TON network with preset
  const addSupportedTonNetwork = (presetKey = 'mainnet') => {
    const presetNetwork = PRESET_TON_NETWORKS[presetKey];
    if (presetNetwork) {
      setEditFormData(prevData => ({
        ...prevData,
        web3Config: {
          ...prevData.web3Config,
          supportedTonNetworks: [...(prevData.web3Config.supportedTonNetworks || []), { ...presetNetwork }]
        }
      }));
    }
  };

  // Remove supported EVM chain
  const removeSupportedChain = (index) => {
    setEditFormData(prevData => ({
      ...prevData,
      web3Config: {
        ...prevData.web3Config,
        supportedChains: prevData.web3Config.supportedChains.filter((_, i) => i !== index)
      }
    }));
  };

  // Remove supported TON network
  const removeSupportedTonNetwork = (index) => {
    setEditFormData(prevData => ({
      ...prevData,
      web3Config: {
        ...prevData.web3Config,
        supportedTonNetworks: prevData.web3Config.supportedTonNetworks.filter((_, i) => i !== index)
      }
    }));
  };

  // Update supported EVM chain
  const updateSupportedChain = (index, field, value) => {
    setEditFormData(prevData => {
      const newChains = [...prevData.web3Config.supportedChains];
      newChains[index] = { ...newChains[index], [field]: value };
      return {
        ...prevData,
        web3Config: {
          ...prevData.web3Config,
          supportedChains: newChains
        }
      };
    });
  };

  // Update supported TON network
  const updateSupportedTonNetwork = (index, field, value) => {
    setEditFormData(prevData => {
      const newNetworks = [...prevData.web3Config.supportedTonNetworks];
      newNetworks[index] = { ...newNetworks[index], [field]: value };
      return {
        ...prevData,
        web3Config: {
          ...prevData.web3Config,
          supportedTonNetworks: newNetworks
        }
      };
    });
  };

  // Update TON network currency
  const updateTonNetworkCurrency = (index, currencyField, value) => {
    setEditFormData(prevData => {
      const newNetworks = [...prevData.web3Config.supportedTonNetworks];
      newNetworks[index] = {
        ...newNetworks[index],
        nativeCurrency: {
          ...newNetworks[index].nativeCurrency,
          [currencyField]: value
        }
      };
      return {
        ...prevData,
        web3Config: {
          ...prevData.web3Config,
          supportedTonNetworks: newNetworks
        }
      };
    });
  };

  const dismissError = () => {
    setError(null);
  };

  const getVersionStatusClass = (version) => {
    if (!webVersionSettings) return 'unknown';
    return webVersionSettings?.webVersion === version ? 'active' : 'inactive';
  };

  const renderWebVersionCard = () => {
    if (!webVersionSettings) return null;

    return (
      <div className="wvm-card">
        <div className="wvm-card-header">
          <h4>Current Configuration</h4>
          <div className="wvm-version-badges">
            <span className={`wvm-version-badge wvm-version-${getVersionStatusClass('web2')}`}>
              Web2 {webVersionSettings?.webVersion === 'web2' ? '(Active)' : ''}
            </span>
            <span className={`wvm-version-badge wvm-version-${getVersionStatusClass('web3')}`}>
              Web3 {webVersionSettings?.webVersion === 'web3' ? '(Active)' : ''}
            </span>
          </div>
        </div>
        <div className="wvm-card-content">
          <div className="wvm-setting-row">
            <span className="wvm-setting-label">Authentication Mode:</span>
            <span className="wvm-setting-value">{webVersionSettings.webVersion?.toUpperCase() || 'Unknown'}</span>
          </div>
          
          {webVersionSettings?.webVersion === 'web2' && (
            <>
              <div className="wvm-setting-row">
                <span className="wvm-setting-label">Email Signup:</span>
                <span className="wvm-setting-value">
                  {webVersionSettings.web2Config?.allowEmailSignup ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="wvm-setting-row">
                <span className="wvm-setting-label">Google Auth:</span>
                <span className="wvm-setting-value">
                  {webVersionSettings.web2Config?.allowGoogleAuth ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="wvm-setting-row">
                <span className="wvm-setting-label">Email Verification:</span>
                <span className="wvm-setting-value">
                  {webVersionSettings.web2Config?.requireEmailVerification ? 'Required' : 'Optional'}
                </span>
              </div>
            </>
          )}

          {webVersionSettings?.webVersion === 'web3' && (
            <>
              <div className="wvm-setting-row">
                <span className="wvm-setting-label">EVM Chains:</span>
                <span className="wvm-setting-value">
                  {webVersionSettings.web3Config?.supportedChains?.length || 0} configured
                </span>
              </div>
              <div className="wvm-setting-row">
                <span className="wvm-setting-label">TON Networks:</span>
                <span className="wvm-setting-value">
                  {webVersionSettings.web3Config?.supportedTonNetworks?.length || 0} configured
                </span>
              </div>
              <div className="wvm-setting-row">
                <span className="wvm-setting-label">Required EVM Chain:</span>
                <span className="wvm-setting-value">
                  {webVersionSettings.web3Config?.requiredChainId || 'Any'}
                </span>
              </div>
              <div className="wvm-setting-row">
                <span className="wvm-setting-label">Required TON Network:</span>
                <span className="wvm-setting-value">
                  {webVersionSettings.web3Config?.requiredTonNetworkId || 'Any'}
                </span>
              </div>
            </>
          )}

          <div className="wvm-setting-row">
            <span className="wvm-setting-label">Dual Auth:</span>
            <span className="wvm-setting-value">
              {webVersionSettings.migrationSettings?.allowDualAuth ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          
          {webVersionSettings?.lastModified && (
            <div className="wvm-last-modified">
              <small>
                Last modified: {new Date(webVersionSettings.lastModified.timestamp).toLocaleString()}
                {webVersionSettings.lastModified.adminIp && ` by ${webVersionSettings.lastModified.adminIp}`}
              </small>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEditDialog = () => {
    if (!showEditDialog) return null;

    return (
      <div className="wvm-modal">
        <div className="wvm-modal-content wvm-modal-large">
          <div className="wvm-modal-header">
            <h4>Edit Web Version Settings</h4>
            <button className="wvm-modal-close" onClick={() => setShowEditDialog(false)}>×</button>
          </div>
          
          <form onSubmit={(e) => { e.preventDefault(); handleUpdateSettings(); }}>
            <div className="wvm-form-group">
              <label className="wvm-form-label">Authentication Mode</label>
              <select 
                className="wvm-form-select"
                value={editFormData.webVersion}
                onChange={(e) => handleInputChange('webVersion', e.target.value)}
              >
                <option value="web2">Web2 (Email/Password)</option>
                <option value="web3">Web3 (Wallet)</option>
              </select>
            </div>

            {editFormData.webVersion === 'web2' && (
              <div className="wvm-section">
                <h5>Web2 Configuration</h5>
                <div className="wvm-checkbox-group">
                  <label className="wvm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.web2Config.allowEmailSignup}
                      onChange={(e) => handleInputChange('web2Config.allowEmailSignup', e.target.checked)}
                    />
                    Allow Email Signup
                  </label>
                  <label className="wvm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.web2Config.allowGoogleAuth}
                      onChange={(e) => handleInputChange('web2Config.allowGoogleAuth', e.target.checked)}
                    />
                    Allow Google Authentication
                  </label>
                  <label className="wvm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.web2Config.requireEmailVerification}
                      onChange={(e) => handleInputChange('web2Config.requireEmailVerification', e.target.checked)}
                    />
                    Require Email Verification
                  </label>
                </div>
              </div>
            )}

            {editFormData.webVersion === 'web3' && (
              <div className="wvm-section">
                <h5>Web3 Configuration</h5>
                <div className="wvm-form-group">
                  <label className="wvm-form-label">WalletConnect Project ID</label>
                  <input
                    type="text"
                    className="wvm-form-input"
                    value={editFormData.web3Config.walletConnectProjectId || ''}
                    onChange={(e) => handleInputChange('web3Config.walletConnectProjectId', e.target.value)}
                    placeholder="Enter WalletConnect Project ID"
                  />
                </div>
                
                <div className="wvm-form-row">
                  <div className="wvm-form-group">
                    <label className="wvm-form-label">Required EVM Chain ID (optional)</label>
                    <input
                      type="number"
                      className="wvm-form-input"
                      value={editFormData.web3Config.requiredChainId || ''}
                      onChange={(e) => handleInputChange('web3Config.requiredChainId', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="Leave empty for any chain"
                    />
                  </div>
                  <div className="wvm-form-group">
                    <label className="wvm-form-label">Required TON Network ID (optional)</label>
                    <input
                      type="text"
                      className="wvm-form-input"
                      value={editFormData.web3Config.requiredTonNetworkId || ''}
                      onChange={(e) => handleInputChange('web3Config.requiredTonNetworkId', e.target.value || null)}
                      placeholder="e.g., mainnet"
                    />
                  </div>
                </div>

                {/* EVM Chains Section */}
                <div className="wvm-chains-section">
                  <div className="wvm-chains-header">
                    <label className="wvm-form-label">EVM Chains</label>
                    <div className="wvm-chain-actions">
                      <select 
                        className="wvm-form-select wvm-select-small"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            addSupportedChain(e.target.value);
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">+ Add EVM Chain</option>
                        <option value="ethereum">Ethereum</option>
                        <option value="polygon">Polygon</option>
                        <option value="bsc">BSC</option>
                        <option value="xaai">XAAI</option>
                        <option value="custom">Custom Chain</option>
                      </select>
                    </div>
                  </div>
                  
                  {editFormData.web3Config.supportedChains?.map((chain, index) => (
                    <div key={index} className="wvm-chain-item">
                      <div className="wvm-chain-badge">
                        <span className="wvm-badge evm">EVM</span>
                      </div>
                      <div className="wvm-chain-inputs">
                        <input
                          type="number"
                          placeholder="Chain ID"
                          value={chain.chainId}
                          onChange={(e) => updateSupportedChain(index, 'chainId', parseInt(e.target.value) || e.target.value)}
                          className="wvm-form-input wvm-input-small"
                        />
                        <input
                          type="text"
                          placeholder="Chain Name"
                          value={chain.name}
                          onChange={(e) => updateSupportedChain(index, 'name', e.target.value)}
                          className="wvm-form-input"
                        />
                        <input
                          type="text"
                          placeholder="RPC URL"
                          value={chain.rpcUrl}
                          onChange={(e) => updateSupportedChain(index, 'rpcUrl', e.target.value)}
                          className="wvm-form-input"
                        />
                        <label className="wvm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={chain.enabled}
                            onChange={(e) => updateSupportedChain(index, 'enabled', e.target.checked)}
                          />
                          Enabled
                        </label>
                      </div>
                      <button 
                        type="button" 
                        className="wvm-btn wvm-btn-danger wvm-btn-small"
                        onClick={() => removeSupportedChain(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>

                {/* TON Networks Section */}
                <div className="wvm-chains-section">
                  <div className="wvm-chains-header">
                    <label className="wvm-form-label">TON Networks</label>
                    <div className="wvm-chain-actions">
                      <select 
                        className="wvm-form-select wvm-select-small"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            addSupportedTonNetwork(e.target.value);
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">+ Add TON Network</option>
                        <option value="mainnet">TON Mainnet</option>
                        <option value="testnet">TON Testnet</option>
                      </select>
                    </div>
                  </div>
                  
                  {editFormData.web3Config.supportedTonNetworks?.map((network, index) => (
                    <div key={index} className="wvm-chain-item">
                      <div className="wvm-chain-badge">
                        <span className="wvm-badge ton">TON</span>
                        {network.isTestnet && <span className="wvm-badge testnet">Testnet</span>}
                      </div>
                      <div className="wvm-chain-inputs wvm-ton-inputs">
                        <input
                          type="text"
                          placeholder="Network ID"
                          value={network.networkId}
                          onChange={(e) => updateSupportedTonNetwork(index, 'networkId', e.target.value)}
                          className="wvm-form-input wvm-input-small"
                        />
                        <input
                          type="text"
                          placeholder="Network Name"
                          value={network.name}
                          onChange={(e) => updateSupportedTonNetwork(index, 'name', e.target.value)}
                          className="wvm-form-input"
                        />
                        <input
                          type="number"
                          placeholder="Workchain"
                          value={network.workchain}
                          onChange={(e) => updateSupportedTonNetwork(index, 'workchain', parseInt(e.target.value) || 0)}
                          className="wvm-form-input wvm-input-small"
                        />
                        <input
                          type="text"
                          placeholder="API URL"
                          value={network.apiUrl}
                          onChange={(e) => updateSupportedTonNetwork(index, 'apiUrl', e.target.value)}
                          className="wvm-form-input"
                        />
                        <input
                          type="text"
                          placeholder="Explorer URL"
                          value={network.blockExplorerUrl}
                          onChange={(e) => updateSupportedTonNetwork(index, 'blockExplorerUrl', e.target.value)}
                          className="wvm-form-input"
                        />
                        <div className="wvm-currency-inputs">
                          <input
                            type="text"
                            placeholder="Currency Name"
                            value={network.nativeCurrency?.name || ''}
                            onChange={(e) => updateTonNetworkCurrency(index, 'name', e.target.value)}
                            className="wvm-form-input"
                          />
                          <input
                            type="text"
                            placeholder="Symbol"
                            value={network.nativeCurrency?.symbol || ''}
                            onChange={(e) => updateTonNetworkCurrency(index, 'symbol', e.target.value)}
                            className="wvm-form-input wvm-input-small"
                          />
                          <input
                            type="number"
                            placeholder="Decimals"
                            value={network.nativeCurrency?.decimals || 9}
                            onChange={(e) => updateTonNetworkCurrency(index, 'decimals', parseInt(e.target.value) || 9)}
                            className="wvm-form-input wvm-input-small"
                          />
                        </div>
                        <label className="wvm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={network.enabled}
                            onChange={(e) => updateSupportedTonNetwork(index, 'enabled', e.target.checked)}
                          />
                          Enabled
                        </label>
                      </div>
                      <button 
                        type="button" 
                        className="wvm-btn wvm-btn-danger wvm-btn-small"
                        onClick={() => removeSupportedTonNetwork(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="wvm-section">
              <h5>Migration Settings</h5>
              <div className="wvm-checkbox-group">
                <label className="wvm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={editFormData.migrationSettings.allowWeb2ToWeb3Migration}
                    onChange={(e) => handleInputChange('migrationSettings.allowWeb2ToWeb3Migration', e.target.checked)}
                  />
                  Allow Web2 to Web3 Migration
                </label>
                <label className="wvm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={editFormData.migrationSettings.allowDualAuth}
                    onChange={(e) => handleInputChange('migrationSettings.allowDualAuth', e.target.checked)}
                  />
                  Allow Dual Authentication (Both Web2 and Web3)
                </label>
              </div>
            </div>

            <div className="wvm-modal-footer">
              <button
                type="button"
                className="wvm-btn wvm-btn-secondary"
                onClick={() => setShowEditDialog(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="wvm-btn wvm-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="wvm-page">
      <div className="content-header">
        <h2>
          <i className="fas fa-cog" style={{ marginRight: '0.75rem' }}></i>
          Web Version Management
        </h2>
        <p>Control authentication modes and user access methods for your application</p>
      </div>
      
      <div className="wvm-container">
        <div className="wvm-header">
          <div className="wvm-header-left">
            <h3 className="wvm-title">Authentication Configuration</h3>
            <p className="wvm-subtitle">Manage Web2 and Web3 authentication settings</p>
          </div>
          <div className="wvm-header-actions">
            <button 
              className="wvm-btn wvm-btn-refresh" 
              onClick={fetchWebVersionSettings}
              disabled={loading}
            >
              <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
              {!loading && <span>Refresh</span>}
            </button>
            <button 
              className="wvm-btn wvm-btn-primary" 
              onClick={() => setShowEditDialog(true)}
              disabled={loading || !webVersionSettings}
            >
              <i className="fas fa-edit"></i>
              <span>Edit Settings</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="wvm-error">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{error}</span>
            <button className="wvm-error-close" onClick={dismissError}>×</button>
          </div>
        )}

        {error && error.includes('endpoints not found') && (
          <div className="wvm-info">
            <i className="fas fa-info-circle"></i>
            <div>
              <div><strong>🚧 Server Implementation Required</strong></div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                The Web Version Management API endpoints are not implemented on your server yet. 
                The UI works in demo mode with local state for testing purposes.
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <strong>Required endpoints to implement:</strong>
                <ul style={{ marginTop: '0.25rem', marginLeft: '1rem' }}>
                  <li><code>GET /api/admin/settings/web-version</code></li>
                  <li><code>PUT /api/admin/settings/web-version</code></li>
                  <li><code>GET /api/auth/auth-method</code></li>
                </ul>
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.8 }}>
                📋 See the provided documentation for complete API specifications.
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="wvm-loading">
            <div className="LoadingSpinner medium">
              <div className="sae-spinner"></div>
              <div className="sae-text">Loading web version settings...</div>
            </div>
          </div>
        ) : (
          renderWebVersionCard()
        )}

        {renderEditDialog()}
      </div>
    </div>
  );
};

export default WebVersionManager;
